import { translations } from '@internal/i18n/translations';
import { usePageContext } from '@internal/page/PageContext';
import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { Machine } from '@internal/utils/machine/Machine';
import { MachinePlaceholderImageMapper } from '@internal/utils/machine/placeholder/MachinePlaceholderImageMapper';
import { useFinancingData } from '@internal/utils/machine/useFinancingData';
import { useMachineImageAltText } from '@internal/utils/machine/useMachineImageAltText';
import { getMediaSrc } from '@internal/utils/media/getMediaSrc';
import { formatPrice } from '@internal/utils/numbers/formatPrice';
import { useMachineUrl } from '@internal/utils/routing/hooks/useMachineUrl';
import { RatioContainer } from '@renderer-ui-library/atoms';
import {
    CardActionArea,
    CardActions,
    CardContent,
    Card as MuiCard,
    Typography,
} from '@renderer-ui-library/mui';
import { ImageRatio } from '@renderer-ui-library/mui/utils/media/ImageRatio';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';
import styles from './serpCard.module.scss';

type Props = {
    children: React.ReactNode;
    machine: Machine;
};

export const SerpCard: React.FC<Props> = React.memo(props => {
    const { locale } = usePageContext();
    const { t } = useTranslation(localeNamespace.common);
    const { getFinancingData } = useFinancingData();
    const financingData = props.machine && getFinancingData(props.machine);
    const { getMachineUrl } = useMachineUrl();
    const url = getMachineUrl(locale, props.machine);
    const { getMachineImageAltText } = useMachineImageAltText();

    const media = useMemo(
        () =>
            props.machine.previewImage
                ? {
                      ...props.machine.previewImage,
                      alternativeText: getMachineImageAltText(props.machine),
                  }
                : props.machine
                  ? MachinePlaceholderImageMapper(
                        props.machine.machineType,
                        'icon',
                    )
                  : null,
        [getMachineImageAltText, props.machine],
    );

    if (!media) {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            <MuiCard variant="outlined">
                <CardActionArea sx={{ justifyContent: 'flex-start' }}>
                    <Link
                        href={url}
                        passHref
                        prefetch={false}
                        className={styles.link}
                    >
                        <RatioContainer ratio={ImageRatio.Landscape}>
                            <img
                                {...getMediaSrc(media, {
                                    ratio: ImageRatio.Landscape,
                                    sizeSet: 'medium',
                                })}
                                loading="lazy"
                                className={styles.media}
                                alt={
                                    media.type === 'machine'
                                        ? media?.alternativeText
                                        : ''
                                }
                            />
                        </RatioContainer>

                        <CardContent className={styles.content}>
                            <Typography
                                variant="h5"
                                title={props.machine.name}
                                className={styles.title}
                            >
                                {props.machine.name}
                            </Typography>
                            {props.children}
                        </CardContent>
                    </Link>
                </CardActionArea>
                {false && financingData && (
                    <Typography variant="h6" color="text.secondary">
                        {/* biome-ignore lint/style/noNonNullAssertion: legacy */}
                        {formatPrice(locale, financingData!.monthlyRate)} /
                        {t(translations.leasingPriceMonthly)}
                    </Typography>
                )}

                {financingData && (
                    <div className={styles.leasingTag}>
                        <Typography variant="body2">
                            {t(translations.leasingAvailableTag).toUpperCase()}
                        </Typography>
                    </div>
                )}

                <CardActions className={styles.shareWrapper}>
                    {/* <ShareButton
            machine={props.machine}
            positionList='top'
            locale={locale}
            variant='small'
          /> */}
                    {props.machine.price ? (
                        <div className={styles.priceContainer}>
                            <Typography
                                variant="h5"
                                color="text.primaryLight"
                                className={styles.shareWrapperPriceHeadline}
                            >
                                {formatPrice(locale, props.machine.price)}
                            </Typography>
                            <span className={styles.vatInfo}>
                                {t(translations.priceTaxInfo)}
                            </span>
                        </div>
                    ) : (
                        <Typography
                            variant="h6"
                            color="text.primaryLight"
                            className={
                                styles.shareWrapperPriceOnRequestHeadline
                            }
                        >
                            {t(translations.priceOnRequest)}
                        </Typography>
                    )}
                </CardActions>
            </MuiCard>
        </div>
    );
});

SerpCard.displayName = 'SerpCard';
